import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  Fab
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import toast from 'react-hot-toast';
import { getApiUrl } from '../config';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category_id: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await fetch(getApiUrl('/api/blog'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('خطا در دریافت پست‌ها');
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(getApiUrl('/api/categories'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('content', formData.content);
    formDataToSend.append('category_id', formData.category_id);
    formDataToSend.append('meta_title', formData.meta_title);
    formDataToSend.append('meta_description', formData.meta_description);
    formDataToSend.append('meta_keywords', formData.meta_keywords);
    
    // اگر عکس جدید انتخاب شده، آن را ارسال کن
    if (formData.image) {
      formDataToSend.append('image', formData.image);
    }
    // اگر در حال ویرایش هستیم و عکس جدید انتخاب نشده، عکس قبلی را حفظ کن
    else if (editingPost && editingPost.image) {
      formDataToSend.append('image', editingPost.image);
    }

    try {
      const url = editingPost 
        ? getApiUrl(`/api/blog/${editingPost.id}`)
        : getApiUrl('/api/blog');
      
      const method = editingPost ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataToSend
      });

      if (res.ok) {
        toast.success(editingPost ? 'پست با موفقیت ویرایش شد' : 'پست با موفقیت ایجاد شد');
        setOpenDialog(false);
        resetForm();
        fetchPosts();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در ذخیره پست');
      }
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('خطا در ذخیره پست');
    }
  };


  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      category_id: post.category_id,
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      image: null // عکس جدید در ابتدا null است
    });
    setImagePreview(post.image ? `${getApiUrl('')}/api/media/download?path=${post.image.split('/').pop()}` : null);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این پست اطمینان دارید؟')) return;

    try {
      const res = await fetch(getApiUrl(`/api/blog/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (res.ok) {
        toast.success('پست با موفقیت حذف شد');
        fetchPosts();
      } else {
        toast.error('خطا در حذف پست');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('خطا در حذف پست');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category_id: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      image: null
    });
    setImagePreview(null);
    setEditingPost(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      fontFamily: 'Vazir',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 4, 
        gap: 2,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: 4,
        p: 3,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 77, 52, 0.1)', // Rafi project primary border
      }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #30d3ae 0%, #26b896 100%)',
          borderRadius: 3,
          p: 1.5,
          boxShadow: '0 4px 12px rgba(48, 211, 174, 0.3)',
        }}>
          <ArticleIcon sx={{ fontSize: 32, color: 'white' }} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ 
            fontFamily: 'Vazir', 
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5,
          }}>
            مدیریت بلاگ
          </Typography>
          <Typography sx={{ 
            fontFamily: 'Vazir', 
            color: '#64748b',
            fontSize: '1rem',
          }}>
            مدیریت و ویرایش پست‌های بلاگ
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ 
        width: '100%', 
        overflow: 'hidden',
        borderRadius: 4,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
      }}>
        <TableContainer sx={{ 
          fontFamily: 'Vazir',
          width: '100%',
          overflowX: 'auto',
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{ 
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              }}>
                <TableCell sx={{ 
                  fontFamily: 'Vazir', 
                  fontWeight: 'bold',
                  color: '#1e293b',
                  fontSize: '1rem',
                  py: 2,
                }}>عنوان</TableCell>
                <TableCell sx={{ 
                  fontFamily: 'Vazir', 
                  fontWeight: 'bold',
                  color: '#1e293b',
                  fontSize: '1rem',
                  py: 2,
                }}>دسته‌بندی</TableCell>
                <TableCell sx={{ 
                  fontFamily: 'Vazir', 
                  fontWeight: 'bold',
                  color: '#1e293b',
                  fontSize: '1rem',
                  py: 2,
                }}>تاریخ ایجاد</TableCell>
                <TableCell sx={{ 
                  fontFamily: 'Vazir', 
                  fontWeight: 'bold',
                  color: '#1e293b',
                  fontSize: '1rem',
                  py: 2,
                }}>عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map((post, index) => (
                <TableRow 
                  key={post.id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: '#f8fafc',
                      transform: 'scale(1.01)',
                    },
                    transition: 'all 0.2s ease',
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafbfc',
                  }}
                >
                  <TableCell sx={{ 
                    fontFamily: 'Vazir',
                    fontWeight: '500',
                    color: '#1e293b',
                    py: 2,
                  }}>{post.title}</TableCell>
                  <TableCell sx={{ 
                    fontFamily: 'Vazir',
                    color: '#64748b',
                    py: 2,
                  }}>
                    <Chip 
                      label={post.category_name}
                      sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontFamily: 'Vazir',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ 
                    fontFamily: 'Vazir',
                    color: '#64748b',
                    py: 2,
                  }}>{formatDate(post.created_at)}</TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="ویرایش پست" sx={{ fontFamily: 'Vazir' }}>
                        <IconButton 
                          onClick={() => handleEdit(post)}
                          size="small"
                          sx={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: 'white',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف پست" sx={{ fontFamily: 'Vazir' }}>
                        <IconButton 
                          onClick={() => handleDelete(post.id)}
                          size="small"
                          sx={{
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: 'white',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="افزودن پست جدید"
        onClick={() => setOpenDialog(true)}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          fontFamily: 'Vazir',
          zIndex: 9999,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}
      >
        <AddIcon />
      </Fab>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(0, 77, 52, 0.1)', // Rafi project primary border
          }
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: 'Vazir', 
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          textAlign: 'center',
          py: 3,
          fontSize: '1.5rem',
        }}>
          {editingPost ? 'ویرایش پست' : 'افزودن پست جدید'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 4 }}>
            <TextField
              fullWidth
              label="عنوان"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              margin="normal"
              required
              sx={{ fontFamily: 'Vazir' }}
              inputProps={{ style: { fontFamily: 'Vazir' } }}
            />
            
            <FormControl fullWidth margin="normal" required>
              <InputLabel sx={{ fontFamily: 'Vazir' }}>دسته‌بندی</InputLabel>
              <Select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                label="دسته‌بندی"
                sx={{ fontFamily: 'Vazir' }}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id} sx={{ fontFamily: 'Vazir' }}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: 'Vazir' }}>
                محتوا *
              </Typography>
              <ReactQuill
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                theme="snow"
                style={{ height: '200px', marginBottom: '50px', fontFamily: 'Vazir' }}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'align': [] }],
                    ['link', 'image'],
                    ['clean']
                  ]
                }}
              />
            </Box>

            <TextField
              fullWidth
              label="عنوان SEO"
              value={formData.meta_title}
              onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
              margin="normal"
              sx={{ fontFamily: 'Vazir' }}
              inputProps={{ style: { fontFamily: 'Vazir' } }}
            />

            <TextField
              fullWidth
              label="توضیحات SEO"
              value={formData.meta_description}
              onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              sx={{ fontFamily: 'Vazir' }}
              inputProps={{ style: { fontFamily: 'Vazir' } }}
            />

            <TextField
              fullWidth
              label="کلمات کلیدی SEO"
              value={formData.meta_keywords}
              onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
              margin="normal"
              sx={{ fontFamily: 'Vazir' }}
              inputProps={{ style: { fontFamily: 'Vazir' } }}
            />

            <Button
              variant="outlined"
              component="label"
              sx={{ mt: 2, fontFamily: 'Vazir' }}
            >
              انتخاب تصویر (حداکثر ۲۰۰ کیلوبایت)
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </Button>

            {imagePreview && (
              <Box sx={{ mt: 2 }}>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  style={{ maxWidth: '200px', maxHeight: '200px' }} 
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderTop: '1px solid rgba(0, 0, 0, 0.05)',
          }}>
            <Button 
              onClick={() => setOpenDialog(false)} 
              sx={{ 
                fontFamily: 'Vazir',
                borderRadius: 3,
                px: 3,
                py: 1.5,
                background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              لغو
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ 
                fontFamily: 'Vazir',
                borderRadius: 3,
                px: 3,
                py: 1.5,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {editingPost ? 'ویرایش' : 'افزودن'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ fontFamily: 'Vazir' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Blog; 